import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Distribucion, type ListadoCuentas, type LlamadoDetalle } from "@/lib/api";
import { formLlamado, validarLlamado, type ErroresLlamado, type FormLlamado } from "@/lib/aportes";
import { separarDistribuciones } from "@/lib/participacion";
import { aPayloadLlamadoAMano, armarPedido, csvPedido, PASO_INICIAL, partesDe, textoPedido, type PayloadLlamadoAMano } from "@/lib/pedidos";
import { guardarArchivo } from "@/lib/reportes";

const SIN_ERRORES: ErroresLlamado = {};

/** Simulador de pedido de capital (sin JSX). Sólo el admin; no guarda nada hasta "Emitir la previsión". */
export function usePedidoTab() {
  const { fid } = useContextoApp();
  const navegar = useNavigate();
  const distribuciones = useApiResource<Distribucion[]>(`/api/fideicomisos/${fid}/distribuciones`);
  const { vigente } = separarDistribuciones(distribuciones.data ?? []);
  const partes = partesDe(vigente);
  // Saldos de las cuentas corrientes (aportes − su parte de los gastos ± lo atrasado): se suman o descuentan si la casilla está marcada.
  const cuentas = useApiResource<ListadoCuentas>(`/api/fideicomisos/${fid}/cuentas`);
  const saldos = Object.fromEntries((cuentas.data?.items ?? []).map((c) => [c.fiduciante_id, c.saldo_usd]));
  const [conSaldos, setConSaldos] = useState(true);

  const [monto, setMonto] = useState("");
  const [paso, setPaso] = useState(PASO_INICIAL);
  const [ajustes, setAjustes] = useState<Record<number, number>>({});
  const [copiado, setCopiado] = useState(false);
  const pedido = armarPedido(monto, partes, paso, ajustes, conSaldos ? saldos : null);

  // "Emitir la previsión": fecha, vencimiento y descripción; el monto es el total pedido y no se edita acá.
  const [dialogoLlamado, setDialogoLlamado] = useState(false);
  const [form, setForm] = useState<FormLlamado>(() => formLlamado());
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const accionEmitir = useAsyncAction((datos: PayloadLlamadoAMano) => api.post<LlamadoDetalle>(`/api/fideicomisos/${fid}/llamados`, datos));
  const errores = validarLlamado({ ...form, monto_usd: pedido?.total ?? "" });
  const abrirLlamado = () => {
    setForm(formLlamado());
    setMostrarErrores(false);
    accionEmitir.limpiarError();
    setDialogoLlamado(true);
  };
  const emitir = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (!pedido || Object.keys(errores).length > 0) return;
    const llamado = await accionEmitir.run(aPayloadLlamadoAMano(pedido, form));
    if (llamado) void navegar(`/llamados/${llamado.id}`);
  };

  // Un monto, un paso o la casilla de saldos nuevos arrancan del redondeo limpio: los ajustes eran sobre el anterior.
  const cambiarMonto = (valor: string) => {
    setMonto(valor);
    setAjustes({});
  };
  const cambiarPaso = (valor: string) => {
    setPaso(Number(valor));
    setAjustes({});
  };
  const cambiarConSaldos = (marcado: boolean) => {
    setConSaldos(marcado);
    setAjustes({});
  };
  const ajustar = (fiduciante_id: number, delta: number) => setAjustes((a) => ({ ...a, [fiduciante_id]: (a[fiduciante_id] ?? 0) + delta }));
  const subirTodos = () => setAjustes((a) => Object.fromEntries(partes.map((p) => [p.fiduciante_id, (a[p.fiduciante_id] ?? 0) + 1])));
  const volverAlRedondeo = () => setAjustes({});

  const descargar = () => {
    if (!pedido) return;
    const blob = new Blob([csvPedido(pedido)], { type: "text/csv;charset=utf-8" });
    guardarArchivo({ blob, nombre: `pedido-de-capital-${pedido.objetivo.replace(/\.00$/, "")}-usd.csv` });
  };
  const copiar = async () => {
    if (!pedido) return;
    try {
      await navigator.clipboard.writeText(textoPedido(pedido));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  };

  return {
    cargando: distribuciones.loading || cuentas.loading,
    errorCarga: distribuciones.error?.message ?? cuentas.error?.message ?? null,
    reintentar: () => {
      distribuciones.recargar();
      cuentas.recargar();
    },
    sinDistribucion: !distribuciones.loading && !distribuciones.error && partes.length === 0,
    conSaldos,
    cambiarConSaldos,
    monto,
    cambiarMonto,
    paso: String(paso),
    cambiarPaso,
    pedido,
    ajustar,
    subirTodos,
    hayAjustes: Object.values(ajustes).some((n) => n !== 0),
    volverAlRedondeo,
    descargar,
    copiar: () => void copiar(),
    copiado,
    puedeArmarLlamado: pedido !== null && pedido.total !== "0.00",
    dialogoLlamado,
    abrirLlamado,
    cerrarLlamado: () => setDialogoLlamado(false),
    form,
    cambiar: <K extends keyof FormLlamado>(campo: K, valor: FormLlamado[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    emitir: (e: FormEvent) => void emitir(e),
    emitiendo: accionEmitir.loading,
    errorEmitir: accionEmitir.error,
  };
}
